'use client';

import Link from 'next/link';
import { useMemo, type ReactNode } from 'react';
import { useLeaderboardModalOptional } from '@/contexts/leaderboard-modal-context';
import { useProfileModalOptional } from '@/contexts/profile-modal-context';
import { ChevronRight, Trophy } from 'lucide-react';
import { AvatarWithFlair } from '@/components/prelaunch/avatar-with-flair';
import { cn, truncateAddress } from '@/lib/utils';
import { useTakeoverPrice } from '@/hooks';
import { sumV2GridSeatsToUsdc6 } from '@/lib/leaderboard/v2-tile-value';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useV2TileLeaderboard,
  type V2LeaderboardUser,
} from '@/features/lobby/leaderboard/use-v2-tile-leaderboard';

type LbEntry = {
  rank: number;
  address: string;
  displayName: string;
  avatar?: string;
  level: number;
  totalSeatValue: bigint;
  isSubject: boolean;
};

function toEntry(
  user: V2LeaderboardUser,
  rank: number,
  subject: string,
  takeoverPriceUsd: number,
): LbEntry {
  let totalSeatValue = 0n;
  const raw = user.totalSeatValue;
  if (raw != null && raw !== '') {
    try {
      const n = BigInt(String(raw));
      if (n > 0n) {
        totalSeatValue = n;
      }
    } catch {
      /* fall through */
    }
  }
  if (totalSeatValue === 0n) {
    totalSeatValue = sumV2GridSeatsToUsdc6(user.gridSeats || [], takeoverPriceUsd);
  }
  const addr = (user.address || user.id || '') as string;
  return {
    rank,
    address: addr,
    displayName: user.displayName || `${addr.slice(0, 8)}...`,
    avatar: user.avatarUrl,
    level: user.level ?? 0,
    totalSeatValue,
    isSubject: subject.toLowerCase() === addr.toLowerCase(),
  };
}

const WINDOW = 4;

interface ProfileLeaderboardSnippetProps {
  /** Profile subject — row matching this address is highlighted. */
  address: string;
  className?: string;
}

/**
 * V2 tile-value window around the subject (same ordering as the in-app leaderboard).
 */
function LeaderboardCtaButton({
  className,
  children,
}: {
  className: string;
  children: ReactNode;
}) {
  const lbModal = useLeaderboardModalOptional();
  if (lbModal) {
    return (
      <button type="button" onClick={() => lbModal.openLeaderboard()} className={className}>
        {children}
      </button>
    );
  }
  // Standalone `/leaderboard` page was retired with the V1 detail-page
  // tree; middleware redirects it to `/?lb=1` and the lobby pops the
  // modal on mount. Link directly to the canonical deep-link so we skip
  // the redirect hop when this snippet renders outside a lobby context
  // (e.g. profile drawers / standalone profile pages).
  return (
    <Link href="/?lb=1" className={className}>
      {children}
    </Link>
  );
}

export function ProfileLeaderboardSnippet({ address, className }: ProfileLeaderboardSnippetProps) {
  const { priceUsd: takeoverPriceUsd = 0 } = useTakeoverPrice();
  const subject = address.toLowerCase();
  // Click on a non-subject row swaps the open profile modal to the
  // clicked address — same modal context the full `LeaderboardRow`
  // (`leaderboard-page-content.tsx:78-97`) and `HoldersPanel` use, so
  // navigation between profiles always feels symmetric. `null` when
  // we're outside any profile modal context (the snippet also renders
  // on the standalone `/profile` route, where rows still link to the
  // referenced profile via the canonical URL fallback below).
  const profileModal = useProfileModalOptional();

  const { data: leaderboardUsers, isLoading, isError } = useV2TileLeaderboard();

  const { entries, windowRows, index } = useMemo(() => {
    const items = leaderboardUsers ?? [];
    const raw: LbEntry[] = items
      .map((u) => toEntry(u, 0, subject, takeoverPriceUsd))
      .filter((e) => e.totalSeatValue > 0n);
    raw.sort((a, b) => {
      if (b.totalSeatValue > a.totalSeatValue) return 1;
      if (b.totalSeatValue < a.totalSeatValue) return -1;
      return 0;
    });
    raw.forEach((e, i) => {
      e.rank = i + 1;
    });
    const idx = raw.findIndex((e) => e.address.toLowerCase() === subject);
    if (idx < 0) {
      return { entries: raw, windowRows: [] as LbEntry[], index: -1 };
    }
    const start = Math.max(0, idx - WINDOW);
    const end = Math.min(raw.length, idx + WINDOW + 1);
    return { entries: raw, windowRows: raw.slice(start, end), index: idx };
  }, [leaderboardUsers, subject, takeoverPriceUsd]);

  if (isLoading) {
    return (
      <div className={cn('px-4 py-3 border-t border-amber-500/10', className)}>
        <div className="flex items-center gap-1.5 text-[9px] font-mono tracking-[0.25em] uppercase text-amber-200/80 mb-2">
          <Trophy className="h-3 w-3" />
          Leaderboard
        </div>
        <div className="space-y-1.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-7 w-full rounded-[4px] border border-amber-500/10 bg-amber-950/20" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || index < 0) {
    return (
      <div className={cn('px-4 py-3 border-t border-amber-500/10', className)}>
        <div className="flex items-center gap-1.5 text-[9px] font-mono tracking-[0.25em] uppercase text-amber-200/80 mb-1.5">
          <Trophy className="h-3 w-3" />
          Leaderboard
        </div>
        <p className="text-[11px] text-zinc-500 font-mono leading-relaxed">
          {isError
            ? 'Could not load rankings.'
            : 'Not on the board yet — hold tiles with value to appear on the leaderboard.'}
        </p>
        <LeaderboardCtaButton className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono text-amber-200/80 hover:text-amber-100">
          View leaderboard
          <ChevronRight className="h-3 w-3" />
        </LeaderboardCtaButton>
      </div>
    );
  }

  return (
    <div className={cn('px-4 py-3 border-t border-amber-500/10', className)}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 text-[9px] font-mono tracking-[0.25em] uppercase text-amber-200/80">
          <Trophy className="h-3 w-3" />
          Leaderboard
        </div>
        <span className="text-[9px] font-mono text-zinc-500 tabular-nums">
          {index + 1} / {entries.length}
        </span>
      </div>
      <ul className="space-y-0.5" aria-label="Leaderboard positions near this profile">
        {windowRows.map((row) => {
          const isHighlight = row.isSubject;
          // Subject row stays a static `<div>` (you're already viewing
          // that profile — clicking is a no-op). Every other row is a
          // button that opens the modal for the clicked address.
          const rowInner = (
            <>
              <span
                className={cn(
                  'w-5 text-[10px] font-mono tabular-nums text-right shrink-0',
                  isHighlight ? 'text-amber-200' : 'text-zinc-500',
                )}
              >
                {row.rank}
              </span>
              <AvatarWithFlair
                avatarUrl={row.avatar}
                address={row.address}
                level={row.level}
                size="xs"
              />
              <div className="min-w-0 flex-1 text-left">
                <div
                  className={cn(
                    'text-[11px] truncate',
                    isHighlight ? 'text-amber-50 font-medium' : 'text-zinc-300',
                  )}
                >
                  {row.displayName}
                </div>
                <div className="text-[9px] font-mono text-zinc-500 truncate">
                  {truncateAddress(row.address)}
                </div>
              </div>
            </>
          );
          if (isHighlight) {
            return (
              <li key={row.address}>
                <div className="flex items-center gap-2 rounded-[4px] px-1.5 py-1 bg-amber-500/15 border border-amber-400/40 shadow-[0_0_0_1px_rgba(251,191,36,0.2)]">
                  {rowInner}
                </div>
              </li>
            );
          }
          if (profileModal) {
            return (
              <li key={row.address}>
                <button
                  type="button"
                  onClick={() => profileModal.openProfile(row.address)}
                  className="w-full flex items-center gap-2 rounded-[4px] px-1.5 py-1 border border-transparent opacity-90 hover:opacity-100 hover:bg-amber-500/[0.06] hover:border-amber-500/20 transition-colors cursor-pointer text-left"
                >
                  {rowInner}
                </button>
              </li>
            );
          }
          // Outside any profile-modal context (no canonical
          // `/profile/[address]` route exists today, see
          // `app/(lobby)/page.tsx` and friends), so the row stays a
          // non-clickable visual — matches the previous behaviour.
          return (
            <li key={row.address}>
              <div className="flex items-center gap-2 rounded-[4px] px-1.5 py-1 border border-transparent opacity-90">
                {rowInner}
              </div>
            </li>
          );
        })}
      </ul>
      <LeaderboardCtaButton className="mt-2 flex w-full items-center justify-center gap-0.5 rounded-[4px] border border-amber-500/20 py-1.5 text-[9px] font-mono tracking-[0.2em] uppercase text-amber-200/80 hover:bg-amber-500/10 hover:text-amber-50 transition-colors">
        Full leaderboard
        <ChevronRight className="h-3 w-3" />
      </LeaderboardCtaButton>
    </div>
  );
}
